import {
  vulcanProvinces,
  fieldIsVulcanOrder,
  fieldProvinceVUL
} from '../../utils/constant';

/**
 * Honest re-implementation of `checkTheOrderIsVulcan` (formStepFMVulcanUtils.js)
 * against the engine's flat `values` map, rather than the app's `formFields`
 * record shape — the original only ever reads two field values via
 * `getFieldValue`, so no `formFields`/`isVisible` machinery is actually needed.
 *
 * `isThisVulcanOrder` is NOT a field-visibility rule: it's a derived flag,
 * computed at submit time and attached to the payload (see payloadAdapter.js),
 * then read back from `orderData.isThisVulcanOrder` in ~20 places across
 * `vulcanHelpers/` (mostly as a prop into the bespoke section components
 * registered via the field registry, T049 — e.g. `isVulcanFlow={orderData.isThisVulcanOrder}`
 * on the Fibre/Copper Internet sections). Those call sites are NOT reimplemented
 * here; they stay inside their existing components, which continue to receive
 * `orderData/isThisVulcanOrder` as a prop exactly as today.
 *
 * @param {Object.<string,*>} values
 * @param {string} yesValue   The active locale's "yes" translation
 *   (`translations.vulcanWebform.yes` — this is locale copy, not a hardcoded
 *   constant, so it's supplied by the caller, not imported here).
 * @returns {boolean}
 */
export function computeIsThisVulcanOrder(values, yesValue) {
  const serviceAddress = values[fieldProvinceVUL];
  const isVulcanOrderAnswer = values[fieldIsVulcanOrder];
  return (
    vulcanProvinces.includes(serviceAddress) && isVulcanOrderAnswer === yesValue
  );
}

/**
 * Factory for the engine's `businessRules` adapter (FR-017a). Vulcan's field
 * visibility is almost entirely declarative already (appearsAfterField /
 * appearsAfterValue, handled natively by the engine's built-in evaluator per
 * the spec's Option B decision), so this adapter is currently a documented
 * no-op extension point rather than an active one.
 *
 * `isThisVulcanOrder` (computed by `computeIsThisVulcanOrder` above) is NOT
 * surfaced through this adapter's return value — the engine's
 * `BusinessRulesAdapter` contract only returns `visibility`/`disabled`/`options`
 * patches, and this flag isn't a field-visibility patch. Instead,
 * `formStepVulcan.js` (T050) and `payloadAdapter.js` call
 * `computeIsThisVulcanOrder` directly wherever the flag is needed (building the
 * payload, threading it as a prop into bespoke sections) — consistent with how
 * the pre-migration code also computes it once and threads it by prop, not
 * through a generic visibility system.
 *
 * The deep, imperative per-field option filtering driven by `isThisVulcanOrder`
 * (e.g. `vulcanHelpers/secureBusinessHelpers.js`'s TSB offer disabling) stays
 * inside those bespoke, registry-registered components (T049) — porting it here
 * would mean re-implementing product UI logic the engine is explicitly not
 * meant to own (spec Clarifications: "the standard scaffold... with several
 * rich custom sections plugged in via the registry").
 *
 * @returns {import('@mobilelive-inc/telus-forms-core').BusinessRulesAdapter}
 */
export function createVulcanBusinessRulesAdapter() {
  return () => ({});
}
